import * as Base from "@base-ui/react"
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from "@lexical/markdown"
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin"
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { mergeRegister, objectKlassEquals } from "@lexical/utils"
import {
  $createTabNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  FORMAT_TEXT_COMMAND,
  isDOMNode,
  isSelectionCapturedInDecoratorInput,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  type LexicalEditor,
  PASTE_COMMAND,
  type TextFormatType,
} from "lexical"
import * as React from "react"
import { toast } from "sonner"
import * as z from "zod/mini"
import type { ContentType } from "../../api/lib/upload.ts"
import { jsonCodec } from "../../shared/codec.ts"
import type * as t from "../../shared/types.ts"
import * as Image from "../lib/image.ts"
import { rpc } from "../lib/rpc.ts"
import * as Video from "../lib/video.ts"
import { Button } from "./Button.tsx"
import { Spinner } from "./Spinner.tsx"

export function MessageEditor(props: MessageEditor.Props) {
  const {
    channelId,
    defaultValue,
    placeholder = "Enter some text",
    onEscape,
    repositoryId,
    state,
    uploadText = "Upload to channel",
  } = props

  const attachments = Attachments.useAttachments({ channelId, repositoryId })
  const disableSubmit = React.useMemo(
    () => attachments.state === "pending",
    [attachments],
  )

  const isDraggingFiles = useDragFiles({
    onDrop: (files) => {
      for (const file of files) attachments.add(file)
    },
  })

  const onSubmit = React.useCallback(
    (editor: LexicalEditor) => async (content: string) => {
      const editorState = editor.getEditorState()
      try {
        attachments.clear()
        return await props.onSubmit(content, attachments.items as never)
      } catch (error) {
        attachments.restore()
        editor.setEditorState(editorState)
        toast.error("Failed to submit message", {
          description: (error as Error).message,
        })
        throw error
      }
    },
    [props.onSubmit, attachments.clear, attachments.items, attachments.restore],
  )

  return (
    <LexicalComposer
      initialConfig={{
        ...(defaultValue
          ? {
              editorState: () => $convertFromMarkdownString(defaultValue, []),
            }
          : {}),
        namespace: "MessageEditor",
        theme,
        onError,
      }}
    >
      <div className="relative rounded-md border border-neutral-6 focus-within:border-accent focus-within:ring-1 focus-within:ring-accent dark:border-neutral-7">
        <div className="scrollbar-neutral-6 dark:scrollbar-neutral-7 scrollbar-width-thin scrollbar-gutter-stable relative max-h-80 overflow-y-auto px-3 pt-2 pr-[68px] pb-8">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="tab-[1] relative resize-none text-[14px] text-neutral-13 leading-[22px] caret-neutral-13 outline-none! dark:text-neutral-12 dark:caret-neutral-12" />
            }
            ErrorBoundary={LexicalErrorBoundary}
            placeholder={
              <div className="pointer-events-none absolute start-3 top-2 inline-block select-none overflow-hidden truncate text-[14px] text-muted leading-[22px]">
                {placeholder}
              </div>
            }
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ShortcutsPlugin
            disableSubmit={disableSubmit}
            hasAttachments={attachments.items.length > 0}
            onEscape={onEscape}
            onSubmit={onSubmit}
            onUpload={attachments.add}
          />
          <ClearEditorPlugin />
          {attachments.items.length > 0 && (
            <div className="pt-2 pb-1">
              <Attachments
                items={attachments.items}
                onRemove={attachments.remove}
              />
            </div>
          )}
        </div>
        <div className="absolute start-1 bottom-1">
          <Toolbar onUpload={(file) => attachments.add(file)} state={state} />
        </div>
        <div className="absolute end-2 bottom-2">
          <MessageEditor.SubmitButton
            disabled={disableSubmit}
            hasAttachments={attachments.items.length > 0}
            onSubmit={onSubmit}
            state={state}
          />
        </div>
      </div>

      <div
        className="pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 opacity-0 backdrop-blur-[2px] transition-opacity duration-150 data-[active=true]:pointer-events-auto data-[active=true]:opacity-100"
        data-active={isDraggingFiles}
      >
        <div className="rounded-md border-2 border-accent border-dashed bg-default/80 p-20">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="font-semibold text-4xl text-default">
              {uploadText}
            </div>
            <div className="text-lg text-muted">
              Release to upload your file(s)
            </div>
          </div>
        </div>
      </div>
    </LexicalComposer>
  )
}

export namespace MessageEditor {
  export type Props = {
    channelId: string
    defaultValue?: string | undefined
    placeholder?: string | React.ReactNode | undefined
    onEscape?: (() => void) | undefined
    onSubmit: (
      content: string,
      attachments: Extract<Attachments.UploadItem, { state: "success" }>[],
    ) => Promise<void>
    repositoryId: string
    state?: "create" | "edit" | undefined
    uploadText?: string | React.ReactNode | undefined
  }

  export function SubmitButton(props: {
    disabled?: boolean | undefined
    hasAttachments?: boolean | undefined
    onSubmit: (editor: LexicalEditor) => (content: string) => Promise<void>
    state: MessageEditor.Props["state"]
  }) {
    const { disabled, hasAttachments, state } = props
    const [editor] = useLexicalComposerContext()
    return (
      <Button
        className="p-1"
        disabled={disabled ?? false}
        height="full"
        onClick={() =>
          editor.read(() =>
            submit(editor, props.onSubmit(editor), hasAttachments),
          )
        }
        size="small"
        variant={state === "edit" ? "default" : "primary"}
      >
        {state === "edit" ? "Save" : "Send"}
      </Button>
    )
  }
}

const theme = {}

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: Error) {
  console.error(error)
}

// TODO: Link shortcut `[]()`
// TODO: Tab/Shift+Tab to indent/dedent
function ShortcutsPlugin(props: ShortcutsPlugin.Props) {
  const { disableSubmit, onEscape, onSubmit, onUpload, hasAttachments } = props
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        (format) => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return false

          const node = selection.anchor.getNode()
          const result = formatText(
            format,
            node.getTextContent(),
            selection.anchor.offset,
            selection.focus.offset,
            Boolean(selection.getTextContent()),
          )

          if (result) {
            const textNode = (() => {
              if ($isTextNode(node)) return node
              // empty so create text node
              const textNode = $createTextNode(result.textContent)
              selection.insertNodes([textNode])
              return textNode
            })()
            textNode.setTextContent(result.textContent)
            selection.setTextNodeRange(
              textNode,
              result.anchorOffset,
              textNode,
              result.focusOffset,
            )
          }

          return true
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          if (event?.metaKey) {
            editor.update(async () => {
              const markdown = $convertToMarkdownString(
                TRANSFORMERS,
                undefined,
                true,
              ).replace(/\\([*_`~])/g, "$1")
              if (markdown.trim().length === 0 && !hasAttachments) return false
              if (disableSubmit) return false
              editor.dispatchCommand(CLEAR_EDITOR_COMMAND, {} as never)
              await onSubmit(editor)(markdown)
              return false
            })
            return true
          }
          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (_event) => {
          onEscape?.()
          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          // if inputs then paste within the input ignore creating a new node on paste event
          if (
            isDOMNode(event.target) &&
            isSelectionCapturedInDecoratorInput(event.target)
          )
            return false

          const selection = $getSelection()
          if (selection !== null) {
            event.preventDefault()
            editor.update(
              () => {
                const selection = $getSelection()
                const clipboardData =
                  objectKlassEquals(event, InputEvent) ||
                  objectKlassEquals(event, KeyboardEvent)
                    ? null
                    : (event as ClipboardEvent).clipboardData
                if (clipboardData != null && selection !== null) {
                  const files = Array.from(clipboardData.files || [])
                  if (files.length > 0) {
                    for (const file of files) onUpload(file)
                    return
                  }

                  // TODO: Get HTML from clipboard and convert to markdown representation
                  const plainString = clipboardData.getData("text/plain")
                  const text =
                    plainString || clipboardData.getData("text/uri-list")
                  if (text != null) {
                    if ($isRangeSelection(selection)) {
                      const parts = text.split(/(\r?\n|\t)/)
                      if (parts[parts.length - 1] === "") {
                        parts.pop()
                      }
                      for (let i = 0; i < parts.length; i++) {
                        const currentSelection = $getSelection()
                        if ($isRangeSelection(currentSelection)) {
                          const part = parts[i]
                          if (part === "\n" || part === "\r\n")
                            currentSelection.insertParagraph()
                          else if (part === "\t")
                            currentSelection.insertNodes([$createTabNode()])
                          else if (part) currentSelection.insertText(part)
                        }
                      }
                    } else selection.insertRawText(text)
                  }
                }
              },
              {
                tag: "paste",
              },
            )
            return true
          }

          return false
        },
        COMMAND_PRIORITY_HIGH,
      ),
    )
  }, [disableSubmit, onEscape, onSubmit, editor, hasAttachments, onUpload])

  return null
}

declare namespace ShortcutsPlugin {
  type Props = {
    disableSubmit?: boolean | undefined
    hasAttachments: boolean
    onEscape?: (() => void) | undefined
    onSubmit: (editor: LexicalEditor) => (content: string) => Promise<void>
    onUpload: (file: File) => void
  }
}

function Attachments(props: Attachments.Props) {
  const { items, onRemove } = props
  return (
    <div className="flex gap-2">
      {items.map((item) => (
        <div className="group relative" key={item.file.name}>
          <Attachments.Upload
            file={item.file}
            onRemove={onRemove}
            state={item.state}
          />
        </div>
      ))}
    </div>
  )
}

namespace Attachments {
  export type UploadItem = t.OneOf<
    | {
        file: File
        state: "pending"
      }
    | {
        file: File & {
          type: ContentType
        }
        key: string
        meta?:
          | {
              blurhash?: string | undefined
              height?: number | undefined
              width?: number | undefined
            }
          | undefined
        state: "success"
      }
  >

  export type Props = {
    items: UploadItem[]
    onRemove: (file: File) => void
  }

  export function useAttachments(props: useAttachments.Props) {
    const [items, setItems] = React.useState<UploadItem[]>([])
    const backupItemsRef = React.useRef<UploadItem[]>([])

    const state = React.useMemo(() => {
      if (items.length === 0) return "idle"
      for (const item of items) if (item.state !== "success") return "pending"
      return "success"
    }, [items])

    return {
      items,
      async add(file: File) {
        setItems((items) => [...items, { file, state: "pending" }])

        const meta = await (async () => {
          if (file.type.startsWith("image/")) {
            const image = await Image.fromFile(file)
            if (!image) return undefined

            const blurhash = await Image.toBlurhash(image)

            return {
              blurhash,
              height: image.naturalHeight,
              width: image.naturalWidth,
            }
          }

          if (file.type.startsWith("video/")) {
            const video = await Video.fromFile(file)
            if (!video) return undefined

            const blurhash = await Video.toBlurhash(video)

            return {
              blurhash,
              height: video.videoHeight,
              width: video.videoWidth,
            }
          }

          return undefined
        })()

        const res = await rpc.uploads.url.$post({
          json: {
            repository_id: props.repositoryId,
            channel_id: props.channelId,
            name: file.name,
            size: file.size,
            type: file.type as never,
          },
        })
        if (!res.ok) this.remove(file)
        if (res.status === 401 || res.status >= 400) {
          const description = await (async () => {
            try {
              const json = z.parse(
                z.object({ error: z.object({ message: z.string() }) }),
                await res.clone().json(),
              )
              const errors = z.decode(
                jsonCodec(z.array(z.object({ message: z.string() }))),
                json.error.message,
              )
              if (errors.length === 0) return res.statusText
              return errors.map((error) => error.message).join(", ")
            } catch (error) {
              return (await res.clone().text()) || res.statusText
            }
          })()
          toast.error("Failed to upload attachment", { description })
          return
        }

        try {
          const json = await res.json()
          await fetch(json.url, {
            body: file,
            headers: json.headers,
            method: json.method,
          })
          setItems((items) =>
            items.map((item) =>
              item.file.name === file.name
                ? {
                    file: item.file as never,
                    meta,
                    key: json.key,
                    state: "success",
                  }
                : item,
            ),
          )
        } catch (error) {
          this.remove(file)
        }
      },

      clear() {
        backupItemsRef.current = items
        setItems([])
      },
      restore() {
        if (backupItemsRef.current.length > 0) {
          setItems(backupItemsRef.current)
          backupItemsRef.current = []
        }
      },
      remove(file: File) {
        setItems((items) =>
          items.filter((item) => item.file.name !== file.name),
        )
      },
      state,
    } as const
  }

  namespace useAttachments {
    export type Props = {
      repositoryId: string
      channelId: string
    }
  }

  export function Upload(props: Upload.Props) {
    const { file, state, onRemove } = props
    return (
      <>
        {file.type.startsWith("image/") ? (
          <div className="size-12">
            <img
              alt=""
              className="size-full rounded-lg object-cover data-[success=false]:opacity-30"
              data-success={state === "success"}
              src={URL.createObjectURL(file)}
            />
          </div>
        ) : file.type.startsWith("video/") ? (
          <div className="size-12">
            <video
              className="size-full rounded-lg object-cover data-[success=false]:opacity-30"
              data-success={state === "success"}
              muted
              preload="metadata"
              src={URL.createObjectURL(file)}
            />
          </div>
        ) : (
          // TODO: Preview other file types
          <div>{file.name}</div>
        )}
        <button
          className="absolute -top-1 -right-1 z-10 flex size-4 items-center justify-center rounded-full border border-neutral-10 bg-neutral-11 text-neutral-0 opacity-0 transition-opacity duration-50 hover:bg-neutral-12 group-hover:opacity-100"
          onClick={() => onRemove(file)}
          type="button"
        >
          <OcticonX16 className="size-3" />
        </button>
        {state === "pending" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner className="size-5" />
          </div>
        )}
      </>
    )
  }

  namespace Upload {
    export type Props = Pick<UploadItem, "file" | "state"> & {
      onRemove: (file: File) => void
    }
  }
}

export function Toolbar(props: Toolbar.Props) {
  const { onUpload, state } = props

  const id = React.useId()

  return (
    <Base.Toolbar.Root className="flex rounded-lg">
      {state !== "edit" && (
        <>
          <Base.Toolbar.Button
            aria-label="Upload file"
            className="text-default/50 hover:text-default"
            nativeButton={false}
            render={
              <Button
                nativeButton={false}
                // biome-ignore lint/a11y/noLabelWithoutControl: explanation
                render={<label htmlFor={id} />}
                size="xsmall"
                variant="invisible"
              />
            }
          >
            <OcticonUpload16 />
          </Base.Toolbar.Button>

          <input
            className="hidden"
            id={id}
            multiple
            onChange={async (event) => {
              const files = event.target.files
              if (!files) return
              for (const file of files) onUpload(file)
            }}
            type="file"
          />
        </>
      )}
    </Base.Toolbar.Root>
  )
}

declare namespace Toolbar {
  export type Props = {
    onUpload: (file: File) => void
    state: MessageEditor.Props["state"]
  }
}

async function submit(
  editor: LexicalEditor,
  onSubmit: (content: string) => Promise<void>,
  hasAttachments?: boolean,
) {
  const markdown = $convertToMarkdownString(
    TRANSFORMERS,
    undefined,
    true,
  ).replace(/\\([*_`~])/g, "$1")

  if (markdown.trim().length === 0 && !hasAttachments) {
    editor.dispatchCommand(CLEAR_EDITOR_COMMAND, {} as never)
    return false
  }

  editor.dispatchCommand(CLEAR_EDITOR_COMMAND, {} as never)
  await onSubmit(markdown)
  return false
}

function formatText(
  format: TextFormatType,
  textContent: string,
  anchorOffset: number,
  focusOffset: number,
  hasRangeSelection = false,
) {
  const formatMarker =
    format in formatMarkers ? formatMarkers[format] : undefined
  if (!formatMarker) return false

  const start = (() => {
    if (hasRangeSelection) {
      const resolvedStart =
        anchorOffset > focusOffset ? focusOffset : anchorOffset
      const markerStart = resolvedStart - formatMarker.length
      if (textContent.slice(markerStart, resolvedStart) === formatMarker)
        return markerStart
      return anchorOffset
    }

    let counter = 0
    const leading = textContent.slice(0, anchorOffset)
    for (let index = 0; index < leading.length; index++) {
      const char = leading[leading.length - index - 1]
      if (char === " ") break
      counter += 1
    }
    return anchorOffset - counter
  })()
  const end = (() => {
    if (hasRangeSelection) {
      const resolvedEnd =
        anchorOffset > focusOffset ? anchorOffset : focusOffset
      const markerEnd = resolvedEnd + formatMarker.length
      if (textContent.slice(resolvedEnd, markerEnd) === formatMarker)
        return markerEnd
      return focusOffset
    }

    let counter = 0
    const trailing = textContent.slice(focusOffset)
    for (let index = 0; index < trailing.length; index++) {
      const char = trailing[index]
      if (char === " ") break
      counter += 1
    }
    return focusOffset + counter
  })()

  const reversed = start > end
  const rangeStart = reversed ? end : start
  const rangeEnd = reversed ? start : end

  const matchedTextContent = textContent.slice(rangeStart, rangeEnd)
  const currentMarker =
    matchedTextContent.startsWith(formatMarker) &&
    matchedTextContent.endsWith(formatMarker)
      ? formatMarker
      : undefined

  const nextTextContent = (() => {
    if (currentMarker) {
      let text = ""
      for (let index = 0; index < textContent.length; index++) {
        const char = textContent[index]
        if (index >= rangeStart && index < rangeStart + currentMarker.length)
          continue
        if (index >= rangeEnd - currentMarker.length && index < rangeEnd)
          continue
        text += char
      }
      return text
    }

    if (textContent.length === 0) return `${formatMarker}${formatMarker}`
    let text = ""
    for (let index = 0; index < textContent.length; index++) {
      if (index === rangeStart) text += formatMarker
      const char = textContent[index]
      text += char
      if (index === rangeEnd - 1) {
        if (rangeStart === rangeEnd) text += formatMarker
        text += formatMarker
      }
    }
    return text
  })()

  const nextAnchorOffset =
    anchorOffset +
    (currentMarker ? -currentMarker.length : (formatMarker?.length ?? 0))
  const nextFocusOffset =
    focusOffset +
    (currentMarker ? -currentMarker.length : (formatMarker?.length ?? 0))

  return {
    textContent: nextTextContent,
    anchorOffset: nextAnchorOffset < 0 ? 0 : nextAnchorOffset,
    focusOffset: nextFocusOffset < 0 ? 0 : nextFocusOffset,
  }
}

const formatMarkers = {
  bold: "**",
  italic: "_",
} as Record<TextFormatType, string>

if (import.meta.vitest) {
  const { expect, test } = import.meta.vitest
  test.each([
    {
      args: ["bold", "foo bar baz", 0, 0, false],
      expected: {
        textContent: "**foo** bar baz",
        anchorOffset: 2,
        focusOffset: 2,
      },
    },
    {
      args: ["bold", "foo bar baz", 1, 1, false],
      expected: {
        textContent: "**foo** bar baz",
        anchorOffset: 3,
        focusOffset: 3,
      },
    },
    {
      args: ["bold", "foo bar baz", 3, 3, false],
      expected: {
        textContent: "**foo** bar baz",
        anchorOffset: 5,
        focusOffset: 5,
      },
    },
    {
      args: ["bold", "foo bar baz", 5, 5, false],
      expected: {
        textContent: "foo **bar** baz",
        anchorOffset: 7,
        focusOffset: 7,
      },
    },
    {
      args: ["bold", "**foo** bar baz", 0, 0, false],
      expected: {
        textContent: "foo bar baz",
        anchorOffset: 0,
        focusOffset: 0,
      },
    },
    {
      args: ["bold", "", 0, 0, false],
      expected: {
        textContent: "****",
        anchorOffset: 2,
        focusOffset: 2,
      },
    },
    {
      args: ["bold", "****", 2, 2, false],
      expected: {
        textContent: "",
        anchorOffset: 0,
        focusOffset: 0,
      },
    },
    {
      args: ["bold", "foo bar baz", 1, 2, true],
      expected: {
        textContent: "f**o**o bar baz",
        anchorOffset: 3,
        focusOffset: 4,
      },
    },
    {
      args: ["bold", "foo ", 4, 4, false],
      expected: {
        textContent: "foo ****",
        anchorOffset: 6,
        focusOffset: 6,
      },
    },
  ] satisfies readonly {
    args: Parameters<typeof formatText>
    expected: ReturnType<typeof formatText>
  }[])("formatText($args.0, $args.1, $args.2, $args.3, $args.4) -> $expected.textContent", ({
    args,
    expected,
  }) => {
    const result = formatText(...(args as Parameters<typeof formatText>))
    expect(result).toStrictEqual(expected)
  })
}

function useDragFiles(props: useDragFiles.Props) {
  const { onDrop } = props

  const [active, setActive] = React.useState(false)
  const dragCounterRef = React.useRef(0)

  const handleDragEnter = React.useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++

    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(
        (item) => item.kind === "file",
      )
      if (hasFiles) setActive(true)
    }
  }, [])

  const handleDragLeave = React.useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--

    if (dragCounterRef.current === 0) setActive(false)
  }, [])

  const handleDragOver = React.useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = React.useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      setActive(false)
      dragCounterRef.current = 0

      const files = Array.from(e.dataTransfer?.files || [])
      onDrop(files)
    },
    [onDrop],
  )

  React.useEffect(() => {
    window.addEventListener("dragenter", handleDragEnter)
    window.addEventListener("dragleave", handleDragLeave)
    window.addEventListener("dragover", handleDragOver)
    window.addEventListener("drop", handleDrop)

    return () => {
      window.removeEventListener("dragenter", handleDragEnter)
      window.removeEventListener("dragleave", handleDragLeave)
      window.removeEventListener("dragover", handleDragOver)
      window.removeEventListener("drop", handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  return active
}

declare namespace useDragFiles {
  export type Props = {
    onDrop: (files: File[]) => void
  }
}
